import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import PaywallModal from "../components/shared/PaywallModal";
import { premiumErrorFeature } from "../utils/subscriptionState";

/**
 * PaywallContext - one global paywall any part of the app can open.
 *
 * Usage: const { openPaywall } = usePaywall(); openPaywall('ai_receipt_scan');
 * The optional feature id lets the paywall highlight what the user just tried
 * to reach. Mounted once near the app root so a single modal serves every
 * trigger (locked features, the trial-ended screen, a Settings entry, or a
 * 402 premium_required response).
 */
const PaywallContext = createContext({ openPaywall: () => {}, closePaywall: () => {} });

export const usePaywall = () => useContext(PaywallContext);

export function PaywallProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState(null);

  const openPaywall = useCallback((f = null) => { setFeature(f); setOpen(true); }, []);
  const closePaywall = useCallback(() => setOpen(false), []);

  // Any endpoint that answers 402 premium_required opens the paywall, then the
  // original error still rejects so the caller's own handling runs. Dormant
  // while subscription gating is off (no 402s are produced), so testers are
  // unaffected.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const f = premiumErrorFeature(err);
        if (f) openPaywall(f);
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [openPaywall]);

  return (
    <PaywallContext.Provider value={{ openPaywall, closePaywall }}>
      {children}
      <PaywallModal open={open} feature={feature} onClose={closePaywall} />
    </PaywallContext.Provider>
  );
}
